import { createSlice } from "@reduxjs/toolkit";

import { getInitialIndex } from "shared/itemLists/CursorItemsCoordinator/functions/cursorFunctions";

import {
  addBlankTaskCaseLogic,
  saveNewTaskThunkAction,
  saveNewTaskCaseLogic,
  commitItemCaseLogic,
  addEmptyDayCaseLogic,
} from "./caseLogic/createActions";

import {
  fetchPlanCaseLogic,
  fetchPlanThunkAction,
  fetchDoneListThunkAction,
} from "./caseLogic/readActions";
import { DateHelper } from "shared/helpers/DateHelper";
import {
  updateItemThunkAction,
  updateItemCaseLogic,
  completeItemThunkAction,
  markItemUndoneThunkAction,
  addChecklistThunkAction,
  removeChecklistThunkAction,
} from "./caseLogic/updateActions";
import {
  moveItemCombinedAction,
  finishMovingItem,
  moveItemCaseLogic,
} from "./caseLogic/moveItem";
import {
  deleteItemThunkAction,
  deleteItemCaseLogic,
} from "./caseLogic/deleteActions";

let today = DateHelper.makeJustDay(new Date());
if (window.Cypress && window.localStorage.getItem("fixedDate")) {
  today = new Date(window.localStorage.getItem("fixedDate"));
}
const initialState = {
  today: DateHelper.dateString(today),
  items: [
    {
      day: DateHelper.dateString(DateHelper.someday()),
      day_name: "Someday",
      type: "day_name",
    },
  ],
  dueItems: [],
  doneItems: [new DateHelper(today).todayDetails()],
  status: "idle",
  insertStatus: {
    hasEmptyDay: false,
    hasBlankTask: false,
    needsScrollAlignment: false,
    isAddingTaskWithEnter: false,
  },
  isChangingDayFromNonScrollEvent: false,
  needsReload: false,
  localIdIncrementor: 1,
  newTask: { position: null, initiated: false, name: "" },
  openItem: { isOpen: false },
  timeCorrectionObject: { isOpen: false },
  movingItem: { id: null },
  needsCursorAdjustment: null,
};

const saveNewTask = saveNewTaskThunkAction;
const fetchDoneList = fetchDoneListThunkAction;
const fetchPlan = fetchPlanThunkAction;
const updateItem = updateItemThunkAction;
const moveItem = moveItemCombinedAction;
const completeItem = completeItemThunkAction;
const markItemUndone = markItemUndoneThunkAction;
const addChecklist = addChecklistThunkAction;
const removeChecklist = removeChecklistThunkAction;
const deleteItem = deleteItemThunkAction;

const itemListsSlice = createSlice({
  name: "itemLists",
  initialState,
  reducers: {
    alignScroll(state, action) {
      const defaults = {
        position: "auto",
        behavior: "auto",
        adjustIndex: false,
      };
      state.insertStatus.needsScrollAlignment = {
        ...defaults,
        ...action.payload,
      };
    },

    addBlankTask(state, action) {
      addBlankTaskCaseLogic(state, action);
    },

    newTaskSet(state, action) {
      if (typeof action.payload === "function") {
        state.newTask = action.payload(state.newTask);
      } else {
        state.newTask = action.payload;
      }
    },

    addEmptyDay(state, action) {
      addEmptyDayCaseLogic(state, action);
    },

    dueItemsSet(state, action) {
      if (typeof action.payload === "function") {
        state.dueItems = action.payload(state.dueItems);
      } else {
        state.dueItems = action.payload;
      }
    },

    setOpenItem(state, action) {
      if (typeof action.payload === "function") {
        state.openItem = action.payload(state.openItem);
      } else {
        state.openItem = action.payload;
      }
    },

    addChecklist(state, action) {
      state.items[action.payload.index].has_checklist = true;
    },

    removeChecklist(state, action) {
      state.items[action.payload.index].has_checklist = false;
    },

    setTimeCorrectionObject(state, action) {
      state.timeCorrectionObject = action.payload;
    },

    setMovingItem(state, action) {
      state.movingItem = action.payload;
    },

    setNewTaskName(state, action) {
      state.newTask.name = action.payload;
    },

    setIsChangingDayFromNonScrollEvent(state, action){
      state.isChangingDayFromNonScrollEvent = action.payload
    },

    setHasBlankTask(state, action) {
      state.insertStatus.hasBlankTask = action.payload;
    },

    setHasEmptyDay(state, action) {
      state.insertStatus.hasEmptyDay = action.payload;
    },

    setNeedsScrollAlignment(state, action) {
      state.insertStatus.needsScrollAlignment = action.payload;
    },

    setNeedsCursorAdjustment(state, action) {
      state.needsCursorAdjustment = action.payload;
    },

    setIsAddingTaskWithEnter(state, action) {
      state.insertStatus.isAddingTaskWithEnter = action.payload;
    },

    moveItem(state, action) {
      moveItemCaseLogic(state, action);
    },

    completeItem(state, action) {
      const { id, isDue } = action.payload;
      const items = isDue ? state.dueItems : state.items;
      if (isDue) {
        state.dueItems = items.filter((item) => item.id !== id);
      } else {
        state.items = items.filter((item) => item.id !== id);
      }
    },

    markItemUndone(state, action) {
      const { id } = action.payload;
      state.doneItems = state.doneItems.filter((item) => item.id !== id);
      state.needsReload = true;
    },

    removeEmptyDay(state, action) {
      state.items = state.items.filter((item) => item.empty !== "true");
      state.insertStatus.hasEmptyDay = false;
    },

    removeBlankTask(state, action) {
      state.items = state.items.filter((item) => {
        return item.blank !== "true";
      });
      state.newTask.initiated = false;
      state.insertStatus.hasBlankTask = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase("itemLists/saveNewTask", (state, action) => {
        saveNewTaskCaseLogic(state, action);
      })
      .addCase("itemLists/saveNewTask.commit", (state, action) => {
        commitItemCaseLogic(state, action);
      })
      .addCase(fetchPlan.pending, (state, action) => {
        state.status = "pending";
      })
      .addCase(fetchPlan.fulfilled, (state, action) => {
        fetchPlanCaseLogic(state, action);
      })
      .addCase(fetchPlan.rejected, (state, action) => {
        state.status = "idle";
      })
      .addCase(fetchDoneList.pending, (state, action) => {
        state.status = "pending";
      })
      .addCase(fetchDoneList.fulfilled, (state, action) => {
        state.status = "idle";
        state.doneItems = action.payload.done_list;
        state.needsCursorAdjustment = {
          cursorIndex: getInitialIndex(action.payload.done_list),
          behavior: "auto",
        };
      })
      .addCase(fetchDoneList.rejected, (state, action) => {
        state.status = "idle";
      })

      .addCase("itemLists/updateItem", (state, action) => {
        updateItemCaseLogic(state, action);
      })
      .addCase("itemLists/updateItem.commit", (state, action) => {
        if (!state.needsReload) {
          state.status = "idle";
        }
      })
      .addCase("itemLists/moveItem.commit", (state, action) => {
        if (!state.needsReload) {
          state.status = "idle";
        }
      })
      .addCase("itemLists/deleteItem", (state, action) => {
        deleteItemCaseLogic(state, action);
      })
      .addCase("itemLists/deleteItem.commit", (state, action) => {
        if (!state.needsReload) {
          state.status = "idle";
        }
      });
  },
});

const resolveTask = (payload) => (dispatch, getState) => {
  const state = getState().itemLists;
  const { newTask } = state;
  const { hasBlankTask } = state.insertStatus;
  const { actions } = itemListsSlice;
  if (newTask.initiated && newTask.name !== "") {
    dispatch(saveNewTask(payload));
  } else if (hasBlankTask) {
    dispatch(actions.removeBlankTask(payload));
  }
};

export const {
  alignScroll,
  dueItemsSet,
  addBlankTask,
  addEmptyDay,
  newTaskSet,
  setOpenItem,
  setMovingItem,
  setTimeCorrectionObject,

  setIsChangingDayFromNonScrollEvent,
  setHasBlankTask,
  setHasEmptyDay,
  setNeedsScrollAlignment,
  setNeedsCursorAdjustment,

  removeBlankTask,
  setIsAddingTaskWithEnter,
  setNewTaskName,
  moveActItem,
  movePlanItem,
  removeEmptyDay,
} = itemListsSlice.actions;

export {
  saveNewTask,
  fetchPlan,
  fetchDoneList,
  updateItem,
  moveItem,
  completeItem,
  markItemUndone,
  finishMovingItem,
  addChecklist,
  deleteItem,
  removeChecklist,
  resolveTask,
};

export default itemListsSlice.reducer;
